import React from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { ReasonPicker } from "./ReasonPicker";
import { SUPERSEDE_PRESETS } from "./reasonPresets";

// v0.6.5 — supersede confirmation dialog, split out alongside
// FreezeConfirmDialog.jsx (500-line cap on Step6FreezeExport.jsx,
// mechanical split, no behaviour change).
export const SupersedeConfirmDialog = ({
  open, onOpenChange, filingRef, calcId,
  reason, setReason, onValidityChange, valid,
  error, busy, onConfirm, carriesFormInputs = false,
}) => {
  const { t } = useTranslation();
  return (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="supersede-confirm-dialog" className="bg-white dark:bg-slate-900">
      <AlertDialogHeader>
        <AlertDialogTitle>{t("ui.freeze.supersede.title", { defaultValue: "Supersede this filing?" })}</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-3 text-xs">
            <p className="text-slate-600 dark:text-slate-300">
              {t("ui.freeze.supersede.body_before", {
                defaultValue: "This runs a brand-new calculation over the same period and data, signs it off, and permanently links it back to",
              })}{" "}
              <span className="tabular-nums text-amber-700 dark:text-amber-300">{filingRef || calcId}</span>
              {t("ui.freeze.supersede.body_after", {
                defaultValue: ". The frozen filing itself is never changed — it stays exactly as filed, marked as superseded, kept for the audit trail. You'll need to freeze and file the new calculation separately.",
              })}
            </p>
            {carriesFormInputs && (
              <p data-testid="supersede-carries-form-inputs" className="font-semibold text-amber-800 dark:text-amber-200">
                {t("ui.freeze.supersede.carries_form", { defaultValue: "The new calculation uses the exchange rates and country filter now on the form, not the ones this filing was made with." })}
              </p>
            )}
            <ReasonPicker
              testid="supersede-reason-picker"
              value={reason}
              onChange={setReason}
              onValidityChange={onValidityChange}
              presets={SUPERSEDE_PRESETS}
              minChars={20}
              placeholder={t("ui.freeze.supersede.reason_placeholder", { defaultValue: "Why does this frozen period need to be redone? (minimum 20 characters)" })}
            />
            {error && (
              <p className="text-red-600 dark:text-red-400" data-testid="supersede-confirm-error">{String(error)}</p>
            )}
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="supersede-confirm-cancel">{t("ui.freeze.confirm.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
        <AlertDialogAction
          data-testid="supersede-confirm-submit"
          onClick={(e) => { e.preventDefault(); onConfirm(); }}
          disabled={busy || !valid}
        >
          {busy ? t("ui.freeze.supersede.busy", { defaultValue: "Superseding…" }) : t("ui.freeze.supersede.submit", { defaultValue: "Supersede & rerun →" })}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  );
};

export default SupersedeConfirmDialog;
